import { Box, Button } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/UserContext";
import { OrganizationContext } from "../../contexts/OrganizationContext";
import Grid2 from "@mui/material/Unstable_Grid2";
import { InputReactHookForm } from "../../components/input/InputReactHookForm";
import { useForm } from "react-hook-form";
import { AutoLocation } from "./AutoLocation";
import SubOrganizationsService from "../../service/sub-organization.service";
import alertService from "../../components/alert/alertService";
import { Site, SubOrganizations } from "../../models/SubOrganization";
import { common, input, organization } from "../../i18n";
import AutoLocationThailand from "./AutoLocationThailand";

type SiteProfileMode = "add" | "edit";

interface Props {
  mode: SiteProfileMode;
}

interface FormValues {
  name: string;
  nameEn: string;
  nameTh: string;
  city: string;
  postalCode: string;
  country: string;
  address: string;
  district: string;
  subDistrict: string;
  subOrganization: string;
  subdistrictRefId: string;
}

export const SiteProfile = (props: Props) => {
  const { t } = useTranslation([common, organization, input], {
    nsMode: "fallback",
  });

  const context = useContext(OrganizationContext);

  if (!context) {
    throw Error();
  }

  const { isAdmin } = useAuth();

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    reset,
    formState: { isValid, isSubmitting },
  } = useForm<FormValues>({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      name: "",
      nameEn: "",
      nameTh: "",
      address: "",
      country: "",
      city: "",
      district: "",
      subDistrict: "",
      postalCode: "",
      subOrganization: "",
      subdistrictRefId: "",
    },
  });

  const onSubmit = async (values: FormValues) => {
    const body = {
      suborganizationRef: subOrganization?.refId,
      name: values.name,
      nameEn: values.nameEn,
      nameTh: values.nameTh,
      subdistrictRef: values.subdistrictRefId,
      addressEn: values.address,
      addressTh: values.address,
    };

    let result: Site | null = null;

    try {
      if (props.mode === "add") {
        result = await SubOrganizationsService.addSiteV2(body);
      } else {
        result = await SubOrganizationsService.editSiteV2(site!.refId, body);
      }
    } catch {
      // error alert already shown by service
    }

    if (result) {
      const temp = [...context.subOrganizations!];
      const index = temp.findIndex((e) => e.refId === subOrganization?.refId);

      if (index > -1) {
        if (props.mode === "add") {
          temp[index].sites.push(result);
        } else {
          const siteIndex = temp[index].sites.findIndex(
            (s) => s.refId === result!.refId,
          );
          temp[index].sites[siteIndex] = {
            ...temp[index].sites[siteIndex],
            ...result,
          };
        }
        context.setSubOrganizations(temp);
        alertService.mixin(t("Successfully"));
        context.setOpenModal(false);
      }
    }
  };

  const [subOrganization, setSubOrganization] = useState<SubOrganizations>();

  const [site, setSite] = useState<Site>();
  const [locationKey, setLocationKey] = useState(0);

  useEffect(() => {
    const item = context.subOrganizations?.find(
      (item) => item.id === context.selectedSubOrganization,
    );

    if (!item) return;

    setSubOrganization(item);

    if (props.mode === "edit") {
      const site = item?.sites.find(
        (s) => s.id.toString() === context.selectedSite,
      );

      if (site) {
        setSite(site);

        reset({
          subOrganization: item.name,
          name: site.name,
          nameEn: site.nameEn ?? "",
          nameTh: site.nameTh ?? "",
          address:
            typeof site.location?.address === "string"
              ? site.location.address
              : site.location?.address?.en ?? "",
          country: site.location?.country?.en ?? "",
          city: site.location?.city?.en ?? "",
          district: site.location?.district?.en ?? "",
          subDistrict: site.location?.subdistrict?.en ?? "",
          postalCode: site.location?.postalCode ?? "",
          subdistrictRefId: site.subdistrictRef,
        });
        setLocationKey((prev) => prev + 1);
      }
    } else {
      reset({
        subOrganization: item.name,
      });
    }
  }, [context.selectedSubOrganization, context.selectedSite]);

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
        <Box
          sx={{
            height: "100%",
            width: "100%",
          }}
          display={"flex"}
          flexDirection={"column"}
          gap={"40px"}
        >
          <Box
            flex={1}
            sx={{
              height: "100%",
              width: "100%",
            }}
          >
            <Grid2
              container
              rowGap={"20px"}
              columnSpacing={"40px"}
              sx={{
                width: "100%",
              }}
            >
              <Grid2 xs={12} md={6}>
                <InputReactHookForm
                  label={t("Organizations")}
                  info={t("Organizations")}
                  name="organization"
                  placeholder={t("Organizations")}
                  control={control}
                  defaultValue={context.organization?.name ?? ""}
                  required
                  disabled
                />
              </Grid2>
              <Grid2 xs={12} md={6}>
                <InputReactHookForm
                  label={t("Sub-Organizations")}
                  info={t("Sub-Organizations")}
                  name="subOrganization"
                  placeholder={t("Sub-Organizations")}
                  control={control}
                  defaultValue={""}
                  required
                  disabled
                />
              </Grid2>

              <Grid2 xs={12} md={6}>
                <InputReactHookForm
                  label={t("Site-Name-Display")}
                  info={t("Site-Name-Display")}
                  name="name"
                  placeholder={t("Site-Name-Display")}
                  control={control}
                  defaultValue={""}
                  required
                  rules={{
                    required: t("This-field-is-required", {
                      param: t("Site-Name-Display"),
                    }),
                    pattern: {
                      value: /^[^,]*$/,
                      message: t("Name-Fails-To-Match-Pattern"),
                    },
                    validate: (text: string) => {
                      const index = subOrganization?.sites.findIndex(
                        (s) =>
                          s.name.trim().toLowerCase() ===
                          text.trim().toLowerCase(),
                      );

                      return index !== undefined &&
                        index >= 0 &&
                        site?.name !== text
                        ? t("SITE_ALREADY_EXISTS")
                        : true;
                    },
                  }}
                  disabled={!isAdmin()}
                />
              </Grid2>

              <Grid2 xs={12} md={6}>
                <InputReactHookForm
                  label={t("Site-Name-English")}
                  info={t("Site-Name-English")}
                  name="nameEn"
                  placeholder={t("Site-Name-English")}
                  control={control}
                  defaultValue=""
                  required
                  rules={{
                    required: t("This-field-is-required", {
                      param: t("Site-Name-English"),
                    }),
                    pattern: {
                      value: /^(?=.*\S)[A-Za-z0-9\s\-_.;\/()]+$/,
                      message: t("English-Only-Allowed"),
                    },
                  }}
                  disabled={!isAdmin()}
                />
              </Grid2>

              <Grid2 xs={12} md={6}>
                <InputReactHookForm
                  label={t("Site-Name-Thai")}
                  info={t("Site-Name-Thai")}
                  name="nameTh"
                  placeholder={t("Site-Name-Thai")}
                  control={control}
                  defaultValue=""
                  required
                  rules={{
                    required: t("This-field-is-required", {
                      param: t("Organization-Name-Thai"),
                    }),
                    pattern: {
                      value: /^(?=.*\S)[ก-๙0-9\s\-_.;\/()]+$/,
                      message: t("Thai-Only-Allowed"),
                    },
                  }}
                  disabled={!isAdmin()}
                />
              </Grid2>

              <AutoLocationThailand
                key={`${site?.subdistrictRef ?? "new"}-${locationKey}`}
                control={control}
                setValue={setValue}
                watch={watch}
                disabled={!isAdmin()}
                initialSubdistrictRefId={site?.subdistrictRef ?? undefined}
              />

              {/* ── Address ── */}
              <Grid2 xs={12} md={6}>
                <InputReactHookForm
                  label={t("Address")}
                  info={t("Address")}
                  name="address"
                  placeholder={t("Address")}
                  control={control}
                  rules={{}}
                  disabled={!isAdmin()}
                />
              </Grid2>

              <Grid2 xs={12}>
                {isAdmin() && (
                  <Button
                    fullWidth
                    variant="contained"
                    color="secondary"
                    type="submit"
                    disabled={!isValid || isSubmitting}
                  >
                    {props.mode === "edit" ? t("Save") : t("Add")}
                  </Button>
                )}
              </Grid2>
            </Grid2>
          </Box>
        </Box>
      </form>
    </>
  );
};
